import { Alert, Box, Card, Container, Typography } from "@mui/material";
import { InfoOutlined, WarningAmber } from "@mui/icons-material";
import { useCustomMutation } from "@refinedev/core";
import { LoadingButton } from "@mui/lab";
import { Country } from "../../store/useMarketplaceStore";
import { useTranslation } from "../../hooks/useTranslation";

interface ReAuthenticationProps {
  invalidMarketplaces: Country[];
}

type AuthorizationResponse = {
  authorizationUrl: string;
};

export const ReAuthentication = ({
  invalidMarketplaces,
}: ReAuthenticationProps) => {
  const { translate } = useTranslation("pages.stores.reAuthentication");
  const { mutate, isLoading } = useCustomMutation<AuthorizationResponse>();

  // Since we're now only checking one marketplace, we expect only one invalid marketplace
  const marketplace = invalidMarketplaces[0];

  const handleReAuthorization = () => {
    mutate(
      {
        url: "authorization/start",
        method: "put",
        values: {
          marketplaceId: marketplace.id,
        },
      },
      {
        onSuccess: (data) => {
          localStorage.setItem("reAuthenticating", "true");
          localStorage.setItem("marketplaceId", marketplace.id.toString());
          window.location.href =
            data.data.authorizationUrl +
            "&state=" +
            marketplace.amazonID +
            "&redirect_uri=" +
            encodeURIComponent(window.location.origin + "/store/create");
        },
      },
    );
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        paddingTop: "40px",
        paddingBottom: "40px",
      }}
    >
      <Card
        sx={{
          padding: "32px",
          width: "100%",
          maxWidth: "600px",
          margin: "0 auto",
        }}
      >
        <Box sx={{ textAlign: "center", marginBottom: "24px" }}>
          <WarningAmber
            sx={{
              fontSize: "64px",
              color: "warning.main",
              marginBottom: "16px",
            }}
          />
          <Typography variant="h4" gutterBottom>
            {translate("title")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {translate("singleStoreDescription")}
          </Typography>
        </Box>

        <Alert
          icon={<InfoOutlined fontSize="inherit" />}
          severity="warning"
          sx={{ marginBottom: "24px" }}
        >
          <Typography variant="body2">
            {translate("tokenExpiredMessage")}
          </Typography>
        </Alert>

        <Box sx={{ marginBottom: "24px" }}>
          <Typography variant="h6" gutterBottom>
            {translate("currentStoreTitle")}
          </Typography>
          <Card
            variant="outlined"
            sx={{
              padding: "20px",
              marginBottom: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "warning.50",
              borderColor: "warning.main",
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {marketplace.storeName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {marketplace.countryName} ({marketplace.countryCode})
              </Typography>
            </Box>
            <LoadingButton
              loading={isLoading}
              variant="contained"
              color="primary"
              size="large"
              onClick={handleReAuthorization}
            >
              {translate("reAuthorizeButton")}
            </LoadingButton>
          </Card>
        </Box>

        <Alert severity="info">
          <Typography variant="body2">
            {translate("singleStoreInstructions")}
          </Typography>
        </Alert>
      </Card>
    </Container>
  );
};
